import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button, Input, Alert } from '../ui';
import { useCountry } from '../../hooks/useCountry';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const { theme, country: countryConfig, isSubdomain } = useCountry();
  const { t, i18n } = useTranslation();
  const colors = theme?.colors || {};

  useEffect(() => {
    if (isSubdomain && countryConfig?.defaultLanguage && i18n.language !== countryConfig.defaultLanguage) {
      i18n.changeLanguage(countryConfig.defaultLanguage);
    }
  }, [isSubdomain, countryConfig, i18n]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/auth/password-reset/`, { email });
      setSuccess(true);
    } catch (err) {
      setError(t('auth.forgotPassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 mb-8 group">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, var(--primary-500), var(--accent-600))', boxShadow: theme ? `0 10px 25px -5px ${colors.primary}40` : undefined }}>
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className={`text-2xl font-bold ${theme ? '' : 'gradient-text'}`} style={theme ? { color: colors.primary } : undefined}>RentyDoc</span>
          </Link>

          {success ? (
            <div className="text-center animate-scale-in">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${colors.primary || '#10b981'}15` }}>
                <svg className="w-10 h-10" style={{ color: colors.primary || '#10b981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('auth.forgotPassword.checkEmailTitle')}</h1>
              <p className="text-gray-600 mb-2">
                {t('auth.forgotPassword.checkEmailText', { email })}
              </p>
              <p className="text-sm text-gray-500 mb-8">{t('auth.forgotPassword.checkSpam')}</p>
              <div className="space-y-4">
                <button onClick={() => { setSuccess(false); setEmail(''); }} className="font-medium" style={{ color: colors.primary || 'var(--primary-600)' }}>
                  {t('auth.forgotPassword.tryDifferentEmail')}
                </button>
                <div className="pt-4 border-t border-gray-200">
                  <Link to="/login" className="text-gray-600 hover:text-gray-900">{t('auth.forgotPassword.backToLogin')}</Link>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.forgotPassword.title')}</h1>
                <p className="text-gray-600">{t('auth.forgotPassword.subtitle')}</p>
              </div>
              {error && (
                <Alert type="error" className="mb-6" dismissible onDismiss={() => setError(null)}>{error}</Alert>
              )}
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  label={t('auth.login.emailLabel')} type="email" name="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required placeholder={t('auth.login.emailPlaceholder')}
                  icon={(props) => (
                    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  )}
                />
                <Button type="submit" variant="primary" fullWidth size="lg" loading={loading}>{t('auth.forgotPassword.sendResetLink')}</Button>
              </form>
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">Or</span></div>
              </div>
              <div className="text-center">
                <Link to="/login" className="inline-flex items-center font-medium" style={{ color: colors.primary || 'var(--primary-600)' }}>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  {t('auth.forgotPassword.backToLogin')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden" style={{ background: theme ? colors.heroBg : undefined }}>
        {!theme && <div className="absolute inset-0 hero-gradient-bg" />}
        <div className="absolute w-96 h-96 rounded-full -top-48 -right-48 blur-3xl float-animation" style={{ backgroundColor: theme ? `${colors.primaryLight}40` : 'rgba(var(--primary-400-rgb), 0.3)' }} />
        <div className="absolute w-80 h-80 rounded-full bottom-1/4 -left-40 blur-3xl float-animation-delayed" style={{ backgroundColor: theme ? `${colors.accent || colors.secondary}30` : 'rgba(var(--accent-400-rgb), 0.3)' }} />
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-16">
          <div className="glass-card p-8 max-w-md text-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, var(--primary-500), var(--accent-600))' }}>
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Secure Password Reset</h2>
            <p className="text-gray-600">We'll send a secure link to your email. The link expires after 24 hours for your protection.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
